/**
 * Hook to fetch BaseTemplate site settings (header URL, title, description)
 * Used by Index page and BaseTemplate.
 *
 * Worker = autoridad para la primera pintada: si el Worker inyectó
 * window.__TEMPLATE_CONFIG__.siteSettings, lo usamos como estado inicial (0 fetch).
 * Tras guardar en admin: `notifySiteSettingsUpdated()` dispara refetch inmediato
 * (mismo tab + cross-tab vía storage). Polling de respaldo cada 12s.
 */

import { isWorkerSiteContentHydrated } from "@/lib/baseTemplateSiteContent";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";
import {
  DEFAULT_SOBRE_MI_TEXT,
  SITE_SETTINGS_CLIENT_DEFAULTS,
  withSiteSettingsDefaults,
} from "@/lib/siteSettingsDefaults";
import {
  SITE_SETTINGS_UPDATED_EVENT,
  SITE_SETTINGS_UPDATED_LS_KEY,
} from "@/lib/siteSettingsRefresh";
import { useState, useEffect } from "react";

export interface SiteSettings {
  headerUrl: string;
  title: string;
  description: string;
  logoUrl?: string;
  sobreMi?: string;
  showSobreMi?: boolean;
  galleryImageUrls?: string[];
  bannerImageUrl?: string;
  footerTagline?: string;
  alternativeGalleryEnabled?: boolean;
  alternativeGalleryUrls?: string[];
  /** false = ocultar sección Mi galería (#portfolio) en BaseTemplate */
  showGallerySection?: boolean;
  /** false = ocultar formulario Tally (#contacto) en BaseTemplate */
  showTallySection?: boolean;
  /** Edad de la artista (gestionable desde el admin; fallback VITE_BRAND_AGE) */
  brandAge?: string;
  /** Hero CTA Override Global (admin site-settings). false = sin override. */
  heroCtaEnabled?: boolean;
  heroCtaLabel?: string;
  heroCtaType?: "link" | "membership";
  heroCtaUrl?: string;
  heroCtaMpUrl?: string;
  heroCtaStripeUrl?: string;
  heroCtaEmoji?: string;
}

/** Fallback cuando ni el Worker ni la API respondieron (brand VITE_BRAND_*). */
const FALLBACK_SETTINGS: SiteSettings = SITE_SETTINGS_CLIENT_DEFAULTS;

/**
 * Hidrata el estado inicial desde el Worker (window.__TEMPLATE_CONFIG__.siteSettings).
 * Si el Worker no inyectó nada (dev local sin Worker, variant=test, etc.), devuelve fallback.
 */
function hydrateInitialSettings(variant: "live" | "test"): SiteSettings {
  // variant=test usa otro ID en DB; el Worker solo cachea la variante live.
  if (variant === "test") return FALLBACK_SETTINGS;
  if (typeof window === "undefined") return FALLBACK_SETTINGS;
  const heroFromWorker = hydrateHeroCtaFromWorkerConfig();
  const injected = getServerTemplateConfig()?.siteSettings;
  if (!injected) return withSiteSettingsDefaults(heroFromWorker);

  // Prefer heroCta fields from injected.siteSettings (now unified in worker injection)
  // fall back to separate top-level heroCta in config for backwards compat.
  const injectedHeroCta = (injected.heroCtaEnabled !== undefined || injected.heroCta_enabled !== undefined)
    ? {
        heroCtaEnabled: injected.heroCtaEnabled ?? injected.heroCta_enabled,
        heroCtaLabel: injected.heroCtaLabel ?? injected.heroCta_label,
        heroCtaType: injected.heroCtaType ?? injected.heroCta_type,
        heroCtaUrl: injected.heroCtaUrl ?? injected.heroCta_url,
        heroCtaMpUrl: injected.heroCtaMpUrl ?? injected.heroCta_mp_url,
        heroCtaStripeUrl: injected.heroCtaStripeUrl ?? injected.heroCta_stripe_url,
        heroCtaEmoji: injected.heroCtaEmoji ?? injected.heroCta_emoji,
      }
    : heroFromWorker;

  return withSiteSettingsDefaults({
    headerUrl: injected.headerUrl,
    title: injected.title,
    description: injected.description,
    logoUrl: injected.logoUrl,
    sobreMi: injected.sobreMi ?? DEFAULT_SOBRE_MI_TEXT,
    showSobreMi: injected.showSobreMi,
    galleryImageUrls: injected.galleryImageUrls,
    bannerImageUrl: injected.bannerImageUrl,
    footerTagline: injected.footerTagline,
    alternativeGalleryEnabled: injected.alternativeGalleryEnabled,
    alternativeGalleryUrls: injected.alternativeGalleryUrls,
    showGallerySection: injected.showGallerySection,
    showTallySection: injected.showTallySection,
    brandAge: injected.brandAge,
    ...injectedHeroCta,
  });
}

/** Worker inyecta heroCta data dentro de siteSettings (unificado) + legacy top-level para compat. */
function hydrateHeroCtaFromWorkerConfig(): Pick<
  SiteSettings,
  | "heroCtaEnabled"
  | "heroCtaLabel"
  | "heroCtaType"
  | "heroCtaUrl"
  | "heroCtaMpUrl"
  | "heroCtaStripeUrl"
  | "heroCtaEmoji"
> {
  if (typeof window === "undefined") return {};
  const workerHero = getServerTemplateConfig()?.heroCta;
  if (!workerHero?.enabled) return {};
  const label = workerHero.label?.trim() ?? "";
  const url = workerHero.url?.trim() ?? "";
  if (!label || !url) return {};
  const type = workerHero.type === "membership" ? "membership" : "link";
  return {
    heroCtaEnabled: true,
    heroCtaLabel: label,
    heroCtaType: type,
    heroCtaEmoji: workerHero.emoji?.trim() || undefined,
    ...(type === "link"
      ? { heroCtaUrl: url }
      : { heroCtaMpUrl: url, heroCtaStripeUrl: url }),
  };
}

function parseHeroCtaFields(data: Record<string, unknown>): Pick<
  SiteSettings,
  | "heroCtaEnabled"
  | "heroCtaLabel"
  | "heroCtaType"
  | "heroCtaUrl"
  | "heroCtaMpUrl"
  | "heroCtaStripeUrl"
  | "heroCtaEmoji"
> {
  const hasHeroCtaFlag =
    "heroCta_enabled" in data || "heroCtaEnabled" in data;
  const enabled =
    data.heroCta_enabled === true || data.heroCtaEnabled === true;
  return {
    heroCtaEnabled: hasHeroCtaFlag ? enabled : undefined,
    heroCtaLabel:
      typeof data.heroCta_label === "string"
        ? data.heroCta_label
        : typeof data.heroCtaLabel === "string"
          ? data.heroCtaLabel
          : undefined,
    heroCtaType:
      data.heroCta_type === "membership" || data.heroCtaType === "membership"
        ? "membership"
        : "link",
    heroCtaUrl:
      typeof data.heroCta_url === "string"
        ? data.heroCta_url
        : typeof data.heroCtaUrl === "string"
          ? data.heroCtaUrl
          : undefined,
    heroCtaMpUrl:
      typeof data.heroCta_mp_url === "string"
        ? data.heroCta_mp_url
        : typeof data.heroCtaMpUrl === "string"
          ? data.heroCtaMpUrl
          : undefined,
    heroCtaStripeUrl:
      typeof data.heroCta_stripe_url === "string"
        ? data.heroCta_stripe_url
        : typeof data.heroCtaStripeUrl === "string"
          ? data.heroCtaStripeUrl
          : undefined,
    heroCtaEmoji:
      typeof data.heroCta_emoji === "string"
        ? data.heroCta_emoji
        : typeof data.heroCtaEmoji === "string"
          ? data.heroCtaEmoji
          : undefined,
  };
}

function parseSettingsData(data: Record<string, unknown>): SiteSettings {
  return withSiteSettingsDefaults({
    headerUrl: (data.headerUrl as string) ?? undefined,
    title: (data.title as string) ?? undefined,
    description: (data.description as string) ?? undefined,
    logoUrl: (data.logoUrl as string) || undefined,
    sobreMi: (data.sobreMi as string) ?? undefined,
    showSobreMi: typeof data.showSobreMi === "boolean" ? data.showSobreMi : undefined,
    galleryImageUrls: Array.isArray(data.galleryImageUrls) ? (data.galleryImageUrls as string[]) : undefined,
    bannerImageUrl: typeof data.bannerImageUrl === "string" && data.bannerImageUrl.trim() ? data.bannerImageUrl : undefined,
    footerTagline: typeof data.footerTagline === "string" && data.footerTagline.trim() ? data.footerTagline.trim() : undefined,
    alternativeGalleryEnabled: typeof data.alternativeGalleryEnabled === "boolean" ? data.alternativeGalleryEnabled : undefined,
    alternativeGalleryUrls: Array.isArray(data.alternativeGalleryUrls) ? (data.alternativeGalleryUrls as string[]) : undefined,
    showGallerySection: typeof data.showGallerySection === "boolean" ? data.showGallerySection : undefined,
    showTallySection: typeof data.showTallySection === "boolean" ? data.showTallySection : undefined,
    brandAge: typeof data.brandAge === "string" && data.brandAge.trim() ? data.brandAge.trim() : undefined,
    ...parseHeroCtaFields(data),
  });
}

export function useSiteSettings(variant: "live" | "test" = "live") {
  // Worker = autoridad para la primera pintada: si inyectó siteSettings, lo usamos
  // ya hidratado y arrancamos sin loading. Caso contrario, fallback + isLoading=true.
  const [settings, setSettings] = useState<SiteSettings>(() => hydrateInitialSettings(variant));
  const hadWorkerHydration =
    variant === "live" &&
    typeof window !== "undefined" &&
    isWorkerSiteContentHydrated();
  const [isLoading, setIsLoading] = useState(!hadWorkerHydration);

  useEffect(() => {
    const buildUrl = (bust = false) => {
      const params = new URLSearchParams();
      if (variant === "test") params.set("variant", "test");
      if (bust) params.set("_t", String(Date.now()));
      const q = params.toString();
      return q ? `/api/site-settings?${q}` : "/api/site-settings";
    };
    let cancelled = false;

    const run = async (soft = false, bust = false) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      try {
        const res = await fetch(buildUrl(bust), { cache: "no-store", signal: controller.signal });
        clearTimeout(timeoutId);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as Record<string, unknown>;
        if (!cancelled) setSettings(parseSettingsData(data));
      } catch (err) {
        clearTimeout(timeoutId);
        if (!cancelled && err instanceof Error && err.name !== "AbortError") {
          console.warn("[useSiteSettings] Fetch error:", err.message);
        }
      } finally {
        if (!cancelled && !soft) setIsLoading(false);
      }
    };

    void run();

    const onVisibility = () => {
      if (cancelled || document.visibilityState !== "visible") return;
      void run(true);
    };
    const onPageShow = (e: PageTransitionEvent) => {
      if (cancelled || !e.persisted) return;
      void run(true);
    };
    const onStorage = (e: StorageEvent) => {
      if (cancelled || e.key !== SITE_SETTINGS_UPDATED_LS_KEY) return;
      void run(true, true);
    };
    const onSiteSettingsUpdated = () => {
      if (!cancelled) void run(true, true);
    };
    // Sin polling por timer (cada instancia montaba un setInterval de 12s → con
    // N componentes usando el hook = spam a /api/site-settings). Refetch event-driven:
    // el admin propaga al instante por SITE_SETTINGS_UPDATED_EVENT + storage (cross-tab),
    // y el visitante refresca al volver a la pestaña (visibility/pageshow).

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("storage", onStorage);
    window.addEventListener(SITE_SETTINGS_UPDATED_EVENT, onSiteSettingsUpdated);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(SITE_SETTINGS_UPDATED_EVENT, onSiteSettingsUpdated);
    };
  }, [variant]);

  return { settings, isLoading };
}
